import {
    Box,
    ClickAwayListener,
    Collapse,
    IconButton,
    List,
    ListItem,
    Link as MuiLink,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FallbackAvatar from '/b2b/common/components/FallbackAvatar';
import Locale from '/b2b/common/components/Locale';
import { MESSAGE_ROLES } from '../../../../state/reducer';
import MarkdownIt from 'markdown-it';
import PersonIcon from '@mui/icons-material/PersonOutline';
import PropTypes from 'prop-types';
import ThinkingIndicator from '../ThinkingIndicator';
import ThumbDownIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUpOffAlt';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import { sanitizeHtml } from '/b2b/common/helpers/DOM';
import styles from './MessageBubble.styles';
import { translate } from '/b2b/common/helpers/i18n';
import withRetranslate from '/b2b/common/hoc/withRetranslate';

const THINKING_TEXTS = [
    'aiAssistant.status.thinking',
    'aiAssistant.status.analyzing',
    'aiAssistant.status.processing',
    'aiAssistant.status.workingOnIt',
    'aiAssistant.status.crunchingDetails',
    'aiAssistant.status.puttingTogether',
    'aiAssistant.status.reviewingContext',
    'aiAssistant.status.almostThere',
    'aiAssistant.status.generatingResponse',
    'aiAssistant.status.draftingReply',
];

const useThinkingText = active => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (!active) return;
        const interval = setInterval(() => {
            setIndex(i => (i + 1) % THINKING_TEXTS.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [active]);

    return THINKING_TEXTS[index];
};

// Up to two initials for an avatar, from whichever name shape the author carries.
const initialsFromName = name =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('');

const formatCitationLabel = (citation, index) => {
    if (citation.title) return citation.title;
    if (citation.url) {
        try {
            const slug = new URL(citation.url).pathname
                .replace(/^\//, '')
                .replace(/-[0-9a-f]{32}$/i, '');
            return slug || citation.url;
        } catch {
            return citation.url;
        }
    }
    return `Source ${index + 1}`;
};

const useStyles = makeStyles(styles);

const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

const isInternalHref = href => {
    try {
        return new URL(href, window.location.origin).origin === window.location.origin;
    } catch {
        return false;
    }
};

const stripHtml = html =>
    new DOMParser().parseFromString(html, 'text/html').body.textContent ?? html;

const defaultLinkRender =
    md.renderer.rules.link_open ||
    function render(tokens, idx, options, env, self) {
        return self.renderToken(tokens, idx, options);
    };

// eslint-disable-next-line camelcase
md.renderer.rules.link_open = function render(tokens, idx, options, env, self) {
    const href = tokens[idx].attrGet('href');
    const isInternal = isInternalHref(href);
    tokens[idx].attrSet('target', isInternal ? '_self' : '_blank');
    tokens[idx].attrSet('rel', 'noopener noreferrer');
    return defaultLinkRender(tokens, idx, options, env, self);
};

const defaultTableOpenRender =
    md.renderer.rules.table_open ||
    ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));
const defaultTableCloseRender =
    md.renderer.rules.table_close ||
    ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));

// Keep sideways scrolling on the table so surrounding prose remains in view.
// eslint-disable-next-line camelcase
md.renderer.rules.table_open = function render(tokens, idx, options, env, self) {
    return `<div class="markdownTableScroll">${defaultTableOpenRender(
        tokens,
        idx,
        options,
        env,
        self
    )}`;
};

// eslint-disable-next-line camelcase
md.renderer.rules.table_close = function render(tokens, idx, options, env, self) {
    return `${defaultTableCloseRender(tokens, idx, options, env, self)}</div>`;
};

const MessageBubble = props => {
    const {
        role,
        content,
        messageId,
        isStreaming,
        onFeedback,
        initialFeedback,
        citations,
        timestamp,
        author,
        isEmployee = false,
        onNavigate,
    } = props;
    const classes = useStyles(props);
    const isAssistant = role === MESSAGE_ROLES.ASSISTANT;
    const thinkingText = useThinkingText(isAssistant && isStreaming && !content);
    const [copied, setCopied] = useState(false);
    const [feedback, setFeedback] = useState(initialFeedback || null);
    const [citationsExpanded, setCitationsExpanded] = useState(false);
    /*
     * Dedupe on whatever identifies the source, strongest identity first: a citation
     * carries a url, or a file and a title. Keying on url alone would collapse the
     * file-only ones into one entry, and keying on title ahead of the file would
     * collapse distinct documents that share a heading (often an empty one).
     */
    const uniqueCitations = citations
        ? [...new Map(citations.map((c, i) => [c.url || c.sourceFile || c.title || i, c])).values()]
        : [];
    const showCitations = isAssistant && !isStreaming && uniqueCitations.length > 0;
    const renderedContent = useMemo(() => {
        if (!isAssistant || !content) return null;
        return sanitizeHtml(md.render(content));
    }, [isAssistant, content]);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(stripHtml(content));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [content]);

    const handleFeedback = useCallback(
        sentiment => {
            setFeedback(sentiment);
            if (onFeedback) {
                onFeedback(messageId, sentiment);
            }
        },
        [messageId, onFeedback]
    );

    const handleMarkdownClick = useCallback(
        event => {
            if (
                !onNavigate ||
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return;
            }
            const link = event.target.closest('a');
            if (!link || !isInternalHref(link.href)) return;

            const url = new URL(link.href, window.location.origin);
            event.preventDefault();
            onNavigate(`${url.pathname}${url.search}${url.hash}`);
        },
        [onNavigate]
    );

    // The agent replies imported into an escalated thread. They don't get the thumbs.
    const isHumanAuthored = isAssistant && isEmployee;
    const isAiAssistant = isAssistant && !isEmployee;

    const authorName = author?.name?.trim();

    // Timestamp lives next to the action buttons for user and Privacy Team
    // messages; AI messages don't show one.
    const formattedTimestamp = timestamp ? moment(timestamp).format('h:mma') : null;
    const showActionsTimestamp = formattedTimestamp && (!isAssistant || isHumanAuthored);

    return (
        <Box className={classes.messageRow} alignItems={isAssistant ? 'flex-start' : 'flex-end'}>
            {isHumanAuthored && (
                <Box className={classes.messageLabel}>
                    <FallbackAvatar src={author?.photoUrl} className={classes.labelAvatar}>
                        {authorName ? (
                            initialsFromName(authorName)
                        ) : (
                            <PersonIcon sx={{ fontSize: '0.9rem' }} />
                        )}
                    </FallbackAvatar>
                    <Typography variant="caption" className={classes.labelText}>
                        {authorName || <Locale path="aiAssistant.sender.privacyTeam" />}
                    </Typography>
                </Box>
            )}
            <Box
                className={classNames(
                    classes.messageGroup,
                    isHumanAuthored && classes.humanMessageGroup
                )}
            >
                <Box
                    className={classNames(
                        isAssistant
                            ? // The bordered bubble marks a person's message, so it keys on
                              // authorship rather than on html.
                              isHumanAuthored
                                ? classes.humanBubble
                                : classes.assistantBubble
                            : classes.userBubble
                    )}
                >
                    {isAssistant ? (
                        <div className={classes.markdown} onClick={handleMarkdownClick}>
                            {content ? (
                                <div dangerouslySetInnerHTML={{ __html: renderedContent }} />
                            ) : null}
                            {isStreaming && !content && (
                                <Box className={classes.thinking}>
                                    <ThinkingIndicator size={20} animate />
                                    <Typography className={classes.thinkingText}>
                                        <Locale path={thinkingText} />
                                    </Typography>
                                </Box>
                            )}
                        </div>
                    ) : (
                        <div className={classes.content}>
                            <Typography>{content}</Typography>
                        </div>
                    )}
                </Box>
                {/* Assistant actions */}
                {isAssistant && !isStreaming && content && (
                    <Box className={classes.actions} justifyContent="flex-start">
                        {showActionsTimestamp && (
                            <Typography variant="caption" className={classes.actionsTimestamp}>
                                {formattedTimestamp}
                            </Typography>
                        )}
                        <Tooltip title={translate(copied ? 'common.copied' : 'common.copy')}>
                            <IconButton size="small" onClick={handleCopy}>
                                <ContentCopyIcon className={classes.actionIcon} />
                            </IconButton>
                        </Tooltip>
                        {isAiAssistant && (
                            <>
                                <Tooltip title={translate('aiAssistant.feedback.helpful')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleFeedback('positive')}
                                        color={feedback === 'positive' ? 'primary' : 'default'}
                                    >
                                        <ThumbUpIcon className={classes.actionIcon} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={translate('aiAssistant.feedback.notHelpful')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleFeedback('negative')}
                                        color={feedback === 'negative' ? 'primary' : 'default'}
                                    >
                                        <ThumbDownIcon className={classes.actionIcon} />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        {showCitations && (
                            <ClickAwayListener onClickAway={() => setCitationsExpanded(false)}>
                                <Box className={classes.citationsWrapper}>
                                    <Box
                                        className={classes.citationsToggle}
                                        onClick={() => setCitationsExpanded(e => !e)}
                                    >
                                        <Typography variant="body2">
                                            {uniqueCitations.length}{' '}
                                            <Locale
                                                path="common.source"
                                                options={{ smart_count: uniqueCitations.length }}
                                            />
                                        </Typography>
                                        {citationsExpanded ? (
                                            <ExpandLessIcon className={classes.citationsIcon} />
                                        ) : (
                                            <ExpandMoreIcon className={classes.citationsIcon} />
                                        )}
                                    </Box>
                                    <Collapse in={citationsExpanded} mountOnEnter unmountOnExit>
                                        <List className={classes.citationsList}>
                                            {uniqueCitations.map((citation, index) => (
                                                <ListItem
                                                    key={citation.url || index}
                                                    disablePadding
                                                    className={classes.citationItem}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        className={classes.citationText}
                                                    >
                                                        <span className={classes.citationNumber}>
                                                            {index + 1}
                                                        </span>{' '}
                                                        {citation.url ? (
                                                            <MuiLink
                                                                href={citation.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                underline="always"
                                                            >
                                                                {formatCitationLabel(
                                                                    citation,
                                                                    index
                                                                )}
                                                            </MuiLink>
                                                        ) : (
                                                            formatCitationLabel(citation, index)
                                                        )}
                                                    </Typography>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Collapse>
                                </Box>
                            </ClickAwayListener>
                        )}
                    </Box>
                )}
                {/* User actions */}
                {!isAssistant && (
                    <Box className={classes.actions} justifyContent="flex-end">
                        {showActionsTimestamp && (
                            <Typography variant="caption" className={classes.actionsTimestamp}>
                                {formattedTimestamp}
                            </Typography>
                        )}
                        <Tooltip title={translate(copied ? 'common.copied' : 'common.copy')}>
                            <IconButton size="small" onClick={handleCopy}>
                                <ContentCopyIcon className={classes.actionIcon} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

MessageBubble.propTypes = {
    role: PropTypes.oneOf([MESSAGE_ROLES.USER, MESSAGE_ROLES.ASSISTANT]).isRequired,
    content: PropTypes.string.isRequired,
    messageId: PropTypes.string,
    isStreaming: PropTypes.bool,
    initialFeedback: PropTypes.string,
    citations: PropTypes.arrayOf(PropTypes.object),
    onFeedback: PropTypes.func,
    timestamp: PropTypes.string,
    author: PropTypes.shape({
        name: PropTypes.string,
        photoUrl: PropTypes.string,
    }),
    isEmployee: PropTypes.bool,
    onNavigate: PropTypes.func,
    shouldRetranslate: PropTypes.string,
};

export default withRetranslate(MessageBubble);
